import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { useAccount, useContractWrite, useWaitForTransaction, useChainId, useContractRead } from 'wagmi';
import '../styles/retro-terminal.css';
import { parseUnits } from 'viem';
import { CONTRACTS, CHAIN_ID, getTxUrl, VOTE_COSTS } from '../config/contracts';
import { 
  FactRegistryABI,
  ERC20ABI
} from '../abis';

interface PendingFact {
  id: number;
  key: string;
  value: string;
  proposer: string;
  timestamp: number;
  oracle: string;
  oracleAddress: string;
  source: string;
  stake: string;
  forVotes: number;
  againstVotes: number;
  status: number; // 0: Pending, 1: Approved, 2: Rejected, 3: Executed
}

const PendingFactsApproval: React.FC = () => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const contracts = CONTRACTS[chainId as keyof typeof CONTRACTS] || CONTRACTS[CHAIN_ID.BASE_SEPOLIA];
  
  // Admin wallet addresses
  const LEXI_WALLET = '0x7A550FB0a013f3e905470b5EA98cdB3316F2c0b6';
  const LIVING_ARCADE_MAIN_WALLET = '0x7A550FB0a013f3e905470b5EA98cdB3316F2c0b6';
  
  // Check if current wallet is admin
  const isAdmin = address && (address.toLowerCase() === LEXI_WALLET.toLowerCase() || address.toLowerCase() === LIVING_ARCADE_MAIN_WALLET.toLowerCase());
  
  const { writeAsync: writeContractAsync, data: hash, isLoading: isPending } = useContractWrite({
    address: CONTRACTS.FACT_REGISTRY,
    abi: FactRegistryABI,
    functionName: 'vote',
  });
  const { isSuccess: isConfirmed } = useWaitForTransaction({ hash });

  // Read pending proposals from FactRegistry
  const { data: pendingProposalsData, error: readError, isLoading: isReading, refetch: refetchPendingProposals } = (useContractRead as any)({
    address: contracts.FACT_REGISTRY,
    abi: FactRegistryABI,
    functionName: 'getPendingProposals',
  });

  // Test basic contract call to verify contract is working
  const { data: proposalCountData, error: countError } = useContractRead({
    address: contracts.FACT_REGISTRY,
    abi: FactRegistryABI,
    functionName: 'proposalCount',
  });

  // Debug logging
  useEffect(() => {
    console.log('=== PendingFactsApproval Debug ===');
    console.log('Chain ID:', chainId);
    console.log('FactRegistry address:', contracts.FACT_REGISTRY);
    console.log('Is reading:', isReading);
    console.log('Proposal count:', proposalCountData);
    console.log('Count error:', countError);
    console.log('Pending proposals data:', pendingProposalsData);
    console.log('Read error:', readError);
    console.log('Data type:', typeof pendingProposalsData);
    if (pendingProposalsData && Array.isArray(pendingProposalsData)) {
      console.log('Data length:', pendingProposalsData.length);
      console.log('First element:', pendingProposalsData[0]);
      console.log('All elements:', pendingProposalsData);
    }
    console.log('================================');
    
    // Handle the case where contract returns empty data (no proposals yet)
    if (pendingProposalsData && Array.isArray(pendingProposalsData) && pendingProposalsData.length === 6) {
      const [proposalIds, proposers, oracleAddresses, keys, values, timestamps] = pendingProposalsData;
      console.log('Processing pending proposals...');
      console.log('Proposal IDs:', proposalIds);
      console.log('Proposers:', proposers);
      console.log('Oracle addresses:', oracleAddresses);
      console.log('Keys:', keys);
      console.log('Values:', values);
      console.log('Timestamps:', timestamps);
      
      if (proposalIds && proposalIds.length > 0) {
        const facts: PendingFact[] = proposalIds.map((id: any, index: number) => ({
          id: id.toString(),
          proposer: proposers[index],
          oracleAddress: oracleAddresses[index],
          factKey: keys[index],
          factValue: values[index],
          timestamp: new Date(Number(timestamps[index]) * 1000),
          forVotes: 0,
          againstVotes: 0,
          status: 'pending'
        }));
        setPendingFacts(facts);
        console.log('✅ Processed', facts.length, 'pending facts');
      } else {
        console.log('ℹ️ No pending proposals found');
        setPendingFacts([]);
      }
    } else if (pendingProposalsData === null || pendingProposalsData === undefined) {
      console.log('ℹ️ Contract call returned null/undefined - no proposals yet');
      setPendingFacts([]);
    }
  }, [chainId, contracts.FACT_REGISTRY, pendingProposalsData, readError, isReading, proposalCountData, countError]);

  const [pendingFacts, setPendingFacts] = useState<PendingFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFact, setSelectedFact] = useState<PendingFact | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);

  // Process pending proposals from FactRegistry
  useEffect(() => {
    const oracleConfigs = [
      { name: 'Identity', address: contracts.IDENTITY_ORACLE },
      { name: 'Geography', address: contracts.GEOGRAPHY_ORACLE },
      { name: 'Philosophy', address: contracts.PHILOSOPHY_ORACLE },
      { name: 'Science', address: contracts.SCIENCE_ORACLE },
      { name: 'History', address: contracts.HISTORY_ORACLE },
      { name: 'Arts', address: contracts.ARTS_ORACLE },
      { name: 'Technology', address: contracts.TECHNOLOGY_ORACLE },
    ];
    if (!pendingProposalsData) {
      setLoading(false);
      return;
    }

    try {
      const [proposalIds, proposers, oracleAddresses, keys, values, timestamps] = pendingProposalsData;
      
      const facts: PendingFact[] = [];
      
      for (let i = 0; i < proposalIds.length; i++) {
        const oracleConfig = oracleConfigs.find(o => o.address.toLowerCase() === oracleAddresses[i].toLowerCase());
        
        facts.push({
          id: Number(proposalIds[i]),
          key: keys[i],
          value: values[i],
          proposer: proposers[i],
          timestamp: Number(timestamps[i]) * 1000, // Convert to milliseconds
          oracle: oracleConfig?.name || 'Unknown',
          oracleAddress: oracleAddresses[i],
          source: '', // Not available in getPendingProposals
          stake: '0.001', // Default stake amount
          forVotes: 0, // Not available in getPendingProposals
          againstVotes: 0, // Not available in getPendingProposals
          status: 0, // Pending
        });
      }
      
      setPendingFacts(facts);
    } catch (error) {
      console.error('Error processing pending facts:', error);
      setPendingFacts([]);
    } finally {
      setLoading(false);
    }
  }, [pendingProposalsData, contracts]);

  const handleVoteFact = async (fact: PendingFact, support: boolean) => {
    if (!isConnected) {
      alert('Please connect your wallet first');
      return;
    }

    try {
      console.log('🚀 Starting vote process for fact:', fact.id, 'Support:', support);
      
      // Check if user is admin (same as LEXI wallet) - skip LIVINGARCADE transfer
      const isAdmin = address?.toLowerCase() === contracts.LEXI_WALLET.toLowerCase();
      
      if (!isAdmin) {
        console.log('💰 Transferring LIVINGARCADE tokens for voting...');
        console.log('Token address:', contracts.LEXI_TOKEN);
        console.log('LEXI wallet:', contracts.LEXI_WALLET);
        console.log('Amount:', VOTE_COSTS.FACT_VOTE);
        
        // Step 1: Transfer LIVINGARCADE tokens to LEXI wallet
        await (writeContractAsync as any)({
          address: contracts.LEXI_TOKEN,
          abi: ERC20ABI,
          functionName: 'transfer',
          args: [contracts.LEXI_WALLET, parseUnits(VOTE_COSTS.FACT_VOTE, 18)],
        });
        
        console.log('✅ LIVINGARCADE token transfer completed, proceeding to vote...');
      } else {
        console.log('👑 Admin user detected - skipping LIVINGARCADE token transfer');
      }
      
      // Step 2: Vote on the fact
      await (writeContractAsync as any)({
        address: contracts.FACT_REGISTRY,
        abi: FactRegistryABI,
        functionName: 'vote',
        args: [fact.id, support],
      });

      console.log('✅ Vote transaction submitted');
      setApprovalDialogOpen(false);
      setSelectedFact(null);
      
      // Refresh the facts list
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error: any) {
      console.error('Voting error:', error);
      let errorMessage = error.message || 'Unknown error';
      
      // Handle common errors
      if (errorMessage.includes('Already voted')) {
        errorMessage = 'You have already voted on this fact proposal.';
      } else if (errorMessage.includes('User rejected')) {
        errorMessage = 'Transaction cancelled - you rejected it in your wallet.';
      } else if (errorMessage.includes('Insufficient')) {
        errorMessage = 'Insufficient LIVINGARCADE token balance for voting.';
      }
      
      alert(`Failed to vote on fact: ${errorMessage}`);
    }
  };

  const handleRejectFact = async (fact: PendingFact) => {
    if (!isConnected) {
      alert('Please connect your wallet first');
      return;
    }
    
    // Use handleVoteFact with support=false (vote against)
    try {
      await handleVoteFact(fact, false);
      setRejectionDialogOpen(false);
      setSelectedFact(null);
    } catch (error: any) {
      console.error('Rejection error:', error);
      alert(`Failed to reject fact: ${error.message || 'Unknown error'}`);
    }
  };

  // Admin functions for force approve/deny
  const handleForceApprove = async (fact: PendingFact) => {
    if (!isConnected || !isAdmin) {
      alert('Admin access required');
      return;
    }

    try {
      console.log('🚀 Force approving fact:', fact.id);
      
      await (writeContractAsync as any)({
        address: contracts.FACT_REGISTRY,
        abi: FactRegistryABI,
        functionName: 'executeProposal',
        args: [fact.id],
      });

      console.log('✅ Force approve transaction submitted');
      
      // Refresh the facts list
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error: any) {
      console.error('Force approve error:', error);
      alert(`Failed to force approve fact: ${error.message || 'Unknown error'}`);
    }
  };

  const handleForceDeny = async (fact: PendingFact) => {
    if (!isConnected || !isAdmin) {
      alert('Admin access required');
      return;
    }

    try {
      console.log('🚀 Force denying fact:', fact.id);
      
      // For force deny, we need to vote against first, then execute
      // First vote against the proposal
      await (writeContractAsync as any)({
        address: contracts.FACT_REGISTRY,
        abi: FactRegistryABI,
        functionName: 'vote',
        args: [fact.id, false], // false = vote against
      });

      console.log('✅ Vote against submitted, now executing proposal');
      
      // Then execute the proposal (it will be rejected due to the vote)
      await (writeContractAsync as any)({
        address: contracts.FACT_REGISTRY,
        abi: FactRegistryABI,
        functionName: 'executeProposal',
        args: [fact.id],
      });

      console.log('✅ Force deny transaction submitted');
      
      // Refresh the facts list
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error: any) {
      console.error('Force deny error:', error);
      alert(`Failed to force deny fact: ${error.message || 'Unknown error'}`);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading pending facts...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h4" 
          component="h1" 
          className="retro-terminal retro-terminal-text"
          sx={{ 
            fontFamily: "'Courier Prime', 'Courier New', monospace",
            fontSize: '24px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            textShadow: '0 0 5px #00ff00',
            color: '#00ff00'
          }}
          gutterBottom
        >
          PENDING FACTS APPROVAL
        </Typography>
        <Typography 
          variant="h6" 
          className="retro-terminal retro-terminal-text"
          sx={{ 
            fontFamily: "'Courier Prime', 'Courier New', monospace",
            fontSize: '14px',
            opacity: 0.8,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: '#00ff00'
          }}
          gutterBottom
        >
          REVIEW AND VOTE ON PROPOSED FACTS
        </Typography>
        {isAdmin && (
          <Alert severity="success" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
            <Typography variant="body2">
              <strong>Admin Mode:</strong> You have admin privileges to force approve/deny facts.
            </Typography>
          </Alert>
        )}
      </Box>

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          Connect your wallet to approve or reject pending facts.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Fact Registry System:</strong> All fact proposals are now managed through the centralized FactRegistry contract.
          <br />
          • Facts are submitted with 0.001 ETH stake for anti-spam protection
          <br />
          • Community members can vote on fact proposals (3-day voting period)
          <br />
          • Approved facts are automatically executed and added to oracles
          <br />
          <br />
          <strong>Voting System:</strong> Decentralized community voting with quorum and approval thresholds.
        </Typography>
      </Alert>

      {isReading && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>Loading pending facts from FactRegistry...</strong>
            <br />
            <strong>Contract Address:</strong> {contracts.FACT_REGISTRY}
            <br />
            <strong>Chain ID:</strong> {chainId}
          </Typography>
        </Alert>
      )}

      {proposalCountData !== undefined && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>Total Proposals in FactRegistry:</strong> {proposalCountData.toString()}
            <br />
            <strong>Contract Address:</strong> {contracts.FACT_REGISTRY}
            <br />
            <strong>Chain ID:</strong> {chainId}
            <br />
            <strong>Status:</strong> {proposalCountData.toString() === '0' ? 'No proposals submitted yet' : `${proposalCountData.toString()} proposals found`}
          </Typography>
        </Alert>
      )}

      {proposalCountData !== undefined && proposalCountData.toString() === '0' && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>No Facts Submitted Yet!</strong>
            <br />
            The FactRegistry shows 0 proposals, which means no facts have been successfully submitted to the contract yet.
            <br />
            <br />
            <strong>Possible Issues:</strong>
            <br />
            1. The fact submission process is not completing the second transaction (fact proposal to FactRegistry)
            <br />
            2. The approval transaction is working, but the fact proposal transaction is failing
            <br />
            3. The fact proposal is being submitted to the wrong contract address
            <br />
            <br />
            <strong>Next Steps:</strong>
            <br />
            - Try submitting a new fact and check if both transactions complete
            <br />
            - Check the console logs for any transaction errors
            <br />
            - Verify the FactRegistry contract address is correct
          </Typography>
        </Alert>
      )}

      {readError && (
        <Alert severity="error" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>Error reading from FactRegistry:</strong> {readError.message}
            <br />
            <strong>Contract Address:</strong> {contracts.FACT_REGISTRY}
            <br />
            <strong>Chain ID:</strong> {chainId}
          </Typography>
        </Alert>
      )}

      {pendingFacts.length === 0 ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            No pending facts to review at this time. All proposed facts have been processed.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                console.log('=== Manual Contract Test ===');
                console.log('Testing getPendingProposals call...');
                console.log('Contract:', contracts.FACT_REGISTRY);
                console.log('Chain ID:', chainId);
                console.log('Current data:', pendingProposalsData);
                console.log('Read error:', readError);
                console.log('===========================');
              }}
            >
              Test Contract Call
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                console.log('🔄 Refreshing pending proposals...');
                refetchPendingProposals();
              }}
            >
              Refresh Data
            </Button>
          </Box>
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {pendingFacts.map((fact, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip 
                      label={fact.oracle} 
                      color="primary" 
                      size="small" 
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(fact.timestamp)}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h6" gutterBottom>
                    {fact.key}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {fact.value}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="caption" color="text.secondary">
                    <strong>Proposed by:</strong> {formatAddress(fact.proposer)}
                    <br />
                    <strong>Oracle:</strong> {fact.oracle}
                    <br />
                    <strong>ID:</strong> {fact.id}
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    onClick={() => {
                      setSelectedFact(fact);
                      setApprovalDialogOpen(true);
                    }}
                    disabled={!isConnected || isPending}
                  >
                    Vote For
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => {
                      setSelectedFact(fact);
                      setRejectionDialogOpen(true);
                    }}
                    disabled={!isConnected || isPending}
                  >
                    Vote Against
                  </Button>
                  {isAdmin && (
                    <>
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={() => handleForceApprove(fact)}
                        disabled={!isConnected || isPending}
                        sx={{ ml: 1 }}
                      >
                        Force Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={() => handleForceDeny(fact)}
                        disabled={!isConnected || isPending}
                        sx={{ ml: 1 }}
                      >
                        Force Deny
                      </Button>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Voting Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vote on Fact Proposal</DialogTitle>
        <DialogContent>
          {selectedFact && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Do you want to vote <strong>FOR</strong> this fact proposal for the <strong>{selectedFact.oracle} Oracle</strong>?
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
                <Typography variant="body2">
                  <strong>Key:</strong> {selectedFact.key}
                </Typography>
                <Typography variant="body2">
                  <strong>Value:</strong> {selectedFact.value}
                </Typography>
                <Typography variant="body2">
                  <strong>Proposed by:</strong> {formatAddress(selectedFact.proposer)}
                </Typography>
                <Typography variant="body2">
                  <strong>Stake:</strong> {selectedFact.stake} ETH
                </Typography>
              </Paper>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Voting Info:</strong> This is a community vote. The fact needs at least 3 votes with 2 approval votes to be approved.
                  Voting period is 3 days from proposal time.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => selectedFact && handleVoteFact(selectedFact, true)}
            disabled={isPending}
          >
            {isPending ? <CircularProgress size={20} /> : 'Vote For'}
          </Button>
          {isAdmin && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => selectedFact && handleForceApprove(selectedFact)}
                disabled={isPending}
                sx={{ ml: 1 }}
              >
                {isPending ? <CircularProgress size={20} /> : 'Force Approve (Admin)'}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => selectedFact && handleForceDeny(selectedFact)}
                disabled={isPending}
                sx={{ ml: 1 }}
              >
                {isPending ? <CircularProgress size={20} /> : 'Force Deny (Admin)'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Vote Against Dialog */}
      <Dialog open={rejectionDialogOpen} onClose={() => setRejectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vote Against Fact Proposal</DialogTitle>
        <DialogContent>
          {selectedFact && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Do you want to vote <strong>AGAINST</strong> this fact proposal for the <strong>{selectedFact.oracle} Oracle</strong>?
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
                <Typography variant="body2">
                  <strong>Key:</strong> {selectedFact.key}
                </Typography>
                <Typography variant="body2">
                  <strong>Value:</strong> {selectedFact.value}
                </Typography>
                <Typography variant="body2">
                  <strong>Proposed by:</strong> {formatAddress(selectedFact.proposer)}
                </Typography>
                <Typography variant="body2">
                  <strong>Stake:</strong> {selectedFact.stake} ETH
                </Typography>
              </Paper>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Voting Info:</strong> Voting against this fact will count as a rejection vote. 
                  If the fact doesn't meet the approval threshold, the proposer will lose their stake.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialogOpen(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedFact && handleRejectFact(selectedFact)}
            disabled={isPending}
          >
            {isPending ? <CircularProgress size={20} /> : 'Vote Against'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Status */}
      {hash && (
        <Alert
          severity={isConfirmed ? 'success' : 'info'}
          sx={{ position: 'fixed', bottom: 20, right: 20, minWidth: 300 }}
        >
          <Typography variant="body2">
            {isConfirmed ? (
              <>
                <strong>Transaction Confirmed!</strong>
                <br />
                <a href={getTxUrl(chainId, hash)} target="_blank" rel="noopener noreferrer">
                  View on BaseScan ↗
                </a>
              </>
            ) : (
              <>
                <strong>Processing...</strong>
                <br />
                Transaction: {hash.slice(0, 10)}...
              </>
            )}
          </Typography>
        </Alert>
      )}
    </Container>
  );
};

export default PendingFactsApproval;
